import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";

export default function Galeri() {
  const [items, setItems] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Galeri Kegiatan"
        titleLines={[
          "Jejak kerja kami",
          <span key="t" className="italic text-gold">
            di lapangan.
          </span>,
        ]}
        description="Dokumentasi penyuluhan hukum, pendampingan masyarakat, dan kegiatan YLBH Sumatera Timur di delapan kabupaten dan kota."
      />

      <section className="mx-auto w-full max-w-7xl px-6 pb-28 lg:px-10">
        {items === null ? (
          <p className="py-20 text-center font-serif text-2xl italic text-[#737373]">Memuat galeri…</p>
        ) : items.length === 0 ? (
          <div
            data-testid="galeri-empty"
            className="border border-white/10 bg-[#0d0d0d] px-8 py-24 text-center"
          >
            <p className="font-serif text-3xl italic text-[#F5F5F5]">
              Dokumentasi kegiatan segera hadir.
            </p>
            <p className="mx-auto mt-4 max-w-md text-sm font-light text-[#A3A3A3]">
              Tim kami sedang mengkurasi foto-foto penyuluhan dan pendampingan
              terbaik untuk ditampilkan di sini.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((g, i) => (
              <FadeUp key={g.id} delay={0.06 * (i % 3)}>
                <button
                  onClick={() => setLightbox(g)}
                  data-testid={`galeri-item-${g.id}`}
                  className="group relative block w-full overflow-hidden border border-white/10 text-left"
                >
                  <img
                    src={imgSrc(g.foto)}
                    alt={g.judul}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="font-serif text-xl leading-snug text-white">{g.judul}</h3>
                    {g.deskripsi && (
                      <p className="mt-1 line-clamp-2 text-xs font-light text-[#C9C9C9]">
                        {g.deskripsi}
                      </p>
                    )}
                  </div>
                </button>
              </FadeUp>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="galeri-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm"
          >
            <button
              onClick={() => setLightbox(null)}
              aria-label="Tutup"
              data-testid="galeri-lightbox-close"
              className="absolute right-6 top-6 border border-white/20 p-2 text-white transition-colors hover:border-gold hover:text-gold"
            >
              <X size={20} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imgSrc(lightbox.foto)}
                alt={lightbox.judul}
                className="max-h-[75vh] w-full border border-white/10 object-contain"
              />
              <figcaption className="mt-4 text-center">
                <p className="font-serif text-xl text-white">{lightbox.judul}</p>
                {lightbox.deskripsi && (
                  <p className="mt-1 text-sm font-light text-[#A3A3A3]">{lightbox.deskripsi}</p>
                )}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaSection
        testid="galeri-cta"
        lines={[
          "Ingin kami hadir",
          <span key="t" className="italic text-gold">
            di wilayah Anda?
          </span>,
        ]}
      />
    </>
  );
}
